import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Tabs,
  Badge,
  DropdownMenu,
  SearchInput,
  Pagination,
  EmptyState,
  Card,
  Button,
} from '@teals/ui-web';
import { useGetAgentsQuery, type Agent } from '../../store/api';
import { useAuth } from '../../hooks';
import { isLuminaAdmin } from '../../utils/luminaAdmin';

type TabId = 'all' | 'system' | 'my' | 'shared' | 'public';

const PAGE_SIZE = 9;

const PageBody = styled.div`
  padding: 2rem 0 4rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageHeaderLeft = styled.div`
  h1 {
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
    color: var(--text-primary);
  }
  p {
    font-size: 0.875rem;
    color: var(--text-muted);
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AgentCard = styled(Card)`
  cursor: pointer;
  transition: all 0.25s;
  border: 1px solid var(--border);
  border-radius: 14px;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: var(--accent);
    transform: translateY(-3px);
    box-shadow: var(--shadow-lg);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const AgentIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const AgentInfo = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 0.75rem;
`;

const AgentName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusDot = styled.span<{ $status: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 0.375rem;
  vertical-align: middle;
  background: ${({ $status }) =>
    $status === 'active'
      ? 'var(--success)'
      : $status === 'issues'
        ? 'var(--error)'
        : 'var(--text-muted)'};
`;

const AgentDesc = styled.p`
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 0 0 1rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.75rem;
  color: var(--text-muted);
  padding-top: 1rem;
  margin-top: auto;
  border-top: 1px solid var(--border);
`;

const CardStats = styled.div`
  display: flex;
  gap: 1rem;
`;

const StatValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const CardActionBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const Attribution = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ActionsWrapper = styled.div`
  position: relative;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const NewAgentCard = styled.div`
  background: var(--bg-surface);
  border: 2px dashed var(--border);
  border-radius: 14px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  color: var(--text-muted);

  &:hover {
    border-color: var(--accent);
    background: var(--accent-subtle);
    color: var(--accent);
  }
`;

const NewAgentLabel = styled.span`
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
`;

const NewAgentHint = styled.span`
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const PlusIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 0.25rem;
  margin: 1.25rem 0 1.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterPill = styled.button<{ $active?: boolean }>`
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  border: 1px solid ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border)')};
  background: ${({ $active }) => ($active ? 'var(--accent-subtle)' : 'var(--bg-surface)')};
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text-muted)')};

  &:hover {
    border-color: var(--accent);
    color: var(--text-secondary);
  }
`;

const FilterCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 999px;
  font-size: 0.625rem;
  font-weight: 600;
  margin-left: 0.25rem;
`;

const Agents: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [pages, setPages] = useState<Record<TabId, number>>({
    all: 1,
    system: 1,
    my: 1,
    shared: 1,
    public: 1,
  });

  const { data: agents = [], isLoading, isError, error } = useGetAgentsQuery();

  const orgId = user?.organizationId;
  const adminUser = isLuminaAdmin(token);

  const buckets = useMemo(() => {
    const my: Agent[] = [];
    const shared: Agent[] = [];
    const pub: Agent[] = [];
    const system: Agent[] = [];

    agents.forEach((a) => {
      if (a.visibility === 'system') {
        system.push(a);
        return;
      }
      const ownerOrg = a.ownerOrgId ?? a.organizationId;
      const isOwner = ownerOrg === orgId;
      const isPublic = a.visibility === 'public';

      if (isOwner) {
        my.push(a);
      } else if (isPublic) {
        pub.push(a);
      } else {
        shared.push(a);
      }
    });
    return { all: agents, my, shared, public: pub, system };
  }, [agents, orgId]);

  const typeCounts = useMemo(() => {
    const list = buckets[activeTab];
    const counts: Record<string, number> = { all: list.length };
    list.forEach((a) => {
      const t = a.agentType ?? 'other';
      counts[t] = (counts[t] ?? 0) + 1;
    });
    return counts;
  }, [buckets, activeTab]);

  const typeLabels: Record<string, string> = {
    all: 'All',
    management: 'Management',
    conversational: 'Conversational',
    task: 'Task',
    general: 'General',
    data: 'Data',
  };

  const filtered = useMemo(() => {
    let list = buckets[activeTab];
    if (typeFilter !== 'all') {
      list = list.filter((a) => a.agentType === typeFilter);
    }
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter((a) => (a.displayName ?? a.agentName)?.toLowerCase().includes(q));
  }, [buckets, activeTab, typeFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(pages[activeTab], totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const setPage = useCallback(
    (page: number) => setPages((prev) => ({ ...prev, [activeTab]: page })),
    [activeTab],
  );

  const handleTabChange = (id: string) => {
    setActiveTab(id as TabId);
    setTypeFilter('all');
    setSearch('');
  };

  const tabs = useMemo(() => {
    const row: { id: TabId; label: string; count: number }[] = [
      { id: 'all', label: 'All', count: buckets.all.length },
    ];
    if (adminUser) {
      row.push({ id: 'system', label: 'System', count: buckets.system.length });
    }
    row.push(
      { id: 'my', label: 'My Agents', count: buckets.my.length },
      { id: 'shared', label: 'Shared with Me', count: buckets.shared.length },
      { id: 'public', label: 'Public', count: buckets.public.length },
    );
    return row;
  }, [adminUser, buckets]);

  const dropdownItems = (agent: Agent) => [
    { id: 'view', label: 'View', onSelect: () => navigate(`/agents/${agent.agentId}`) },
    { id: 'test', label: 'Test', onSelect: () => navigate(`/agents/${agent.agentId}/test`) },
    { id: 'clone', label: 'Clone', onSelect: () => navigate(`/agents/${agent.agentId}/clone`) },
    { id: 'settings', label: 'Settings', onSelect: () => navigate(`/agents/${agent.agentId}/settings`) },
  ];

  return (
    <PageBody className="fade-in">
      <Container>
        <PageHeader>
          <PageHeaderLeft>
            <h1>Agents</h1>
            <p>Manage and configure your AI agents</p>
          </PageHeaderLeft>
          <HeaderActions>
            <SearchInput
              placeholder="Search agents..."
              value={search}
              onChange={setSearch}
              style={{ minWidth: 280 }}
            />
            <Button variant="primary" onClick={() => navigate('/agents/new')}>
              New Agent
            </Button>
          </HeaderActions>
        </PageHeader>

        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleTabChange}
        />

        {!isLoading && !isError && (
          <FilterBar>
            {Object.entries(typeCounts)
              .sort(([a], [b]) => (a === 'all' ? -1 : b === 'all' ? 1 : 0))
              .map(([type, count]) => (
                <FilterPill
                  key={type}
                  $active={typeFilter === type}
                  onClick={() => setTypeFilter(type)}
                >
                  {typeLabels[type] ?? type}
                  <FilterCount>{count}</FilterCount>
                </FilterPill>
              ))}
          </FilterBar>
        )}

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: 'var(--text-muted)', fontSize: '0.9375rem' }}>
            Loading agents...
          </div>
        )}

        {isError && (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: 'var(--error)', fontSize: '0.9375rem' }}>
            Failed to load agents.{' '}
            {error && 'status' in error ? `(${error.status})` : ''}
          </div>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <EmptyState
            title={
              activeTab === 'all'
                ? 'No agents available'
                : activeTab === 'system'
                  ? 'No system agents'
                  : activeTab === 'my'
                    ? 'No agents yet'
                    : activeTab === 'shared'
                      ? 'Nothing shared with you'
                      : 'No public agents'
            }
            description={
              activeTab === 'all'
                ? 'Create your first agent or browse public agents to get started.'
                : activeTab === 'system'
                  ? 'Platform agents appear here for administrators.'
                  : activeTab === 'my'
                    ? 'Create your first agent to get started.'
                    : activeTab === 'shared'
                      ? 'When someone shares an agent with you, it will appear here.'
                      : 'Public agents from the community will appear here.'
            }
          />
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <>
            <Grid>
              {(activeTab === 'all' || activeTab === 'my') && (
                <NewAgentCard onClick={() => navigate('/agents/new')}>
                  <PlusIcon>+</PlusIcon>
                  <NewAgentLabel>Create New Agent</NewAgentLabel>
                  <NewAgentHint>Describe what you need in natural language</NewAgentHint>
                </NewAgentCard>
              )}

              {paged.map((agent) => {
                const visibility = agent.visibility ?? 'private';
                const ownerName = (agent as any).ownerName;
                const sharedBy = (agent as any).sharedBy;
                const sessionCount = (agent as any).sessionCount ?? 0;

                return (
                  <AgentCard
                    key={agent.agentId}
                    elevation="sm"
                    padding="1.5rem"
                    onClick={() => navigate(`/agents/${agent.agentId}`)}
                  >
                    <CardTop>
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <AgentIcon>{(agent.displayName ?? agent.agentName)[0]}</AgentIcon>
                        <AgentInfo>
                          <AgentName>
                            <StatusDot $status={agent.status} />
                            {agent.displayName ?? agent.agentName}
                          </AgentName>
                          {activeTab === 'shared' && sharedBy && (
                            <Attribution>Shared by {sharedBy}</Attribution>
                          )}
                          {activeTab === 'public' && ownerName && (
                            <Attribution>By {ownerName}</Attribution>
                          )}
                        </AgentInfo>
                      </div>
                      <ActionsWrapper onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu items={dropdownItems(agent)} />
                      </ActionsWrapper>
                    </CardTop>

                    <AgentDesc>{agent.description}</AgentDesc>

                    <BadgeRow>
                      <Badge
                        variant={
                          visibility === 'public'
                            ? 'info'
                            : visibility === 'system'
                              ? 'warning'
                              : 'neutral'
                        }
                        size="sm"
                      >
                        {visibility === 'public'
                          ? 'Public'
                          : visibility === 'system'
                            ? 'System'
                            : 'Private'}
                      </Badge>
                      {agent.config?.llmModel && (
                        <Badge variant="neutral" size="sm">
                          {agent.config.llmModel}
                        </Badge>
                      )}
                      <Badge
                        variant={agent.status === 'active' ? 'success' : agent.status === 'issues' ? 'error' : 'neutral'}
                        size="sm"
                      >
                        {agent.status}
                      </Badge>
                    </BadgeRow>

                    <CardMeta>
                      <CardStats>
                        <span><StatValue>{sessionCount}</StatValue> sessions</span>
                        <span>{new Date(agent.createdAt).toLocaleDateString()}</span>
                      </CardStats>
                      <CardActions onClick={(e) => e.stopPropagation()}>
                        <CardActionBtn title="Chat">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                          </svg>
                        </CardActionBtn>
                        <CardActionBtn title="Settings">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                          </svg>
                        </CardActionBtn>
                      </CardActions>
                    </CardMeta>
                  </AgentCard>
                );
              })}
            </Grid>

            {totalPages > 1 && (
              <PaginationWrapper>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </PaginationWrapper>
            )}
          </>
        )}
      </Container>
    </PageBody>
  );
};

export default Agents;
